import { BanknotesIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';

import { ChequeWizard } from '@/components/postgrid/cheque-wizard';
import { BaseDetailDrawer } from '@/components/postgrid/common/BaseDetailDrawer';
import { MailItemDetailsContent } from '@/components/postgrid/common/MailItemDetailsContent';
import type { ReadDetailedPostgridCheque } from '@/services/api/generated/postgridService';
import {
  useGetChequeDetailedQuery,
  usePostgridChequesCancelMutation,
  usePostgridEventsListQuery,
} from '@/services/api/postgridService';

interface ChequeDetailsProps {
  chequeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (cheque: ReadDetailedPostgridCheque) => void;
  onCancel?: (cheque: ReadDetailedPostgridCheque) => void;
  onUpdate?: (cheque: ReadDetailedPostgridCheque) => void;
  className?: string;
}

const formatCurrencyUSD = (amount: number | string | undefined): string => {
  if (amount === undefined || amount === null) return '$0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numAmount);
};

const ChequeDetails = ({
  chequeId,
  open,
  onOpenChange,
  onEdit,
  onCancel,
  onUpdate,
  className,
}: ChequeDetailsProps) => {
  const {
    data: cheque,
    isLoading,
    error,
  } = useGetChequeDetailedQuery(chequeId, {
    skip: !chequeId,
  });

  const [cancelCheque, { isLoading: isCancelling }] =
    usePostgridChequesCancelMutation();

  const { data: events = [] } = usePostgridEventsListQuery(
    { objectId: chequeId },
    { skip: !chequeId },
  );

  const handleCancel = async () => {
    if (!cheque || isCancelling) return;

    try {
      await cancelCheque({ id: cheque.id as string }).unwrap();
      toast.success('Cheque cancelled');
      onCancel?.(cheque);
      onUpdate?.({
        ...cheque,
        status: 'cancelled',
      } as ReadDetailedPostgridCheque);
      onOpenChange(false);
    } catch (err) {
      const errorMessage =
        (err as { data?: { error?: string } })?.data?.error ||
        'Failed to cancel cheque';
      toast.error(errorMessage);
    }
  };

  const renderIcon = () => (
    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
      <BanknotesIcon className="w-5 h-5 text-white" />
    </div>
  );

  const renderTitle = (item: ReadDetailedPostgridCheque) => {
    const recipient =
      item.to_contact?.company_name ||
      [item.to_contact?.first_name, item.to_contact?.last_name]
        .filter(Boolean)
        .join(' ') ||
      'Recipient';
    return `Check to ${recipient}`;
  };

  const renderDescription = (item: ReadDetailedPostgridCheque) =>
    `${formatCurrencyUSD(item.amount)} • Check #${item.number || 'N/A'}`;

  const renderContent = (item: ReadDetailedPostgridCheque) => (
    <MailItemDetailsContent
      entityType="cheque"
      status={item.status}
      live={item.live}
      mailingClass={item.mailing_class}
      trackingNumber={item.tracking_number}
      carrierTracking={item.carrier_tracking}
      toContact={item.to_contact}
      fromContact={item.from_contact}
      dateSent={item.date_sent}
      dateDelivered={item.date_delivered}
      expectedDeliveryDate={item.expected_delivery_date}
      finalPdfUrl={item.final_pdf_url}
      attachments={item.attachments}
      matterId={item.matter_id}
      returnEnvelopeId={item.return_envelope_id}
      postgridId={item.postgrid_id}
      internalId={item.id as string}
      events={events}
      onCancel={handleCancel}
      isCancelling={isCancelling}
      cheque={{
        amount: item.amount,
        memo: item.memo,
        number: item.number,
        dateCleared: item.date_cleared,
        chequeType: item.cheque_type,
        bankAccountId: item.bank_account_id,
      }}
    />
  );

  const renderEditForm = (
    item: ReadDetailedPostgridCheque,
    onSuccess: (updatedItem: ReadDetailedPostgridCheque) => void,
    onFormCancel: () => void,
  ) => (
    <ChequeWizard
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      matterId={(item as any).matter_id ?? ''}
      onSuccess={(created) =>
        onSuccess(created as unknown as ReadDetailedPostgridCheque)
      }
      onCancel={onFormCancel}
    />
  );

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <BaseDetailDrawer
      data={cheque}
      isLoading={isLoading}
      error={error as Error | null}
      open={open}
      onClose={handleClose}
      onEdit={onEdit}
      onUpdate={onUpdate}
      renderIcon={renderIcon}
      renderTitle={renderTitle}
      renderDescription={renderDescription}
      renderContent={renderContent}
      renderEditForm={renderEditForm}
      className={className}
      loadingMessage="Loading cheque details..."
      errorMessage="Error loading cheque details"
      editTitle="Edit Cheque"
      editDescription="Update cheque information and settings"
      canEdit={(item: ReadDetailedPostgridCheque) => item.status === 'creating'}
      editButtonLabel="Edit"
    />
  );
};

export { ChequeDetails };
